import React, { useEffect, useRef, useState } from "react";
import { ChevronIcon, FilterIcon, RefreshIcon, SearchIcon } from "../icons";

// `options` is null while it is still loading. Options with a `color` render as a status badge.
const CheckList = ({ options, selected, onToggle }) => {
  if (!options) return <Loading />;
  return (
    <div className="filter-option-list activity-filter-list">
      {options.length === 0 && <span className="filter-empty-msg">No options available</span>}
      {options.map((opt) => (
        <label key={opt.value} className="filter-check-row">
          <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => onToggle(opt.value)} />
          {opt.color ? (
            <span className="filter-opt-label hiring-status-badge" style={{ color: opt.color, background: `${opt.color}18` }}>
              {opt.label}
            </span>
          ) : (
            <span className="filter-opt-label">{opt.label}</span>
          )}
          {opt.count != null && <span className="filter-opt-count">{opt.count}</span>}
        </label>
      ))}
    </div>
  );
};

const Loading = () => (
  <div className="filter-acc-loading">
    <div className="spinner-border" style={{ width: "1rem", height: "1rem", color: "#F75891" }} />
  </div>
);

// Same layout as the Advanced Search "Stage status" filter: each stage with its own statuses.
const StageStatusGroups = ({ groups, selected, onToggle }) => {
  if (!groups) return <Loading />;
  if (groups.length === 0) return <span className="filter-empty-msg">No stages configured</span>;
  return (
    <div className="stage-filter-list">
      {groups.map((stage) => (
        <div key={stage.value} className="stage-filter-item">
          <div className="stage-filter-head">
            <span>{stage.label} {stage.count != null && <span className="stage-total-cnt">({stage.count})</span>}</span>
          </div>
          {stage.statuses.length > 0 ? (
            <div className="question-option-check-list stage-status-check-list">
              {stage.statuses.map((opt) => (
                <label key={opt.value} className="question-option-check-row">
                  <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => onToggle(opt.value)} />
                  {opt.color ? (
                    <span className="q-opt-label hiring-status-badge" style={{ color: opt.color, background: `${opt.color}18` }}>
                      {opt.label}
                    </span>
                  ) : (
                    <span className="q-opt-label">{opt.label}</span>
                  )}
                  {opt.count != null && <span className="q-opt-count">{opt.count}</span>}
                </label>
              ))}
            </div>
          ) : (
            <span className="filter-empty-msg">No candidates on this stage</span>
          )}
        </div>
      ))}
    </div>
  );
};

// Left filter panel for the Activity-wise view. Every change refetches straight away,
// so there's no Apply button — just Reset. Sections start closed; a section opens on its own
// when it gets an active filter (e.g. from an Insights link), and can still be closed.
const ActivityFilterSidebar = ({ filters, onChange, onToggle, onReset, options, activeCount }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggleSection = (key) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const sections = [
    {
      key: "search", label: "Candidate", count: filters.search.trim() ? 1 : 0,
      body: (
        <div className="sidebar-search-v2">
          <SearchIcon />
          <input type="text" placeholder="Search candidate name..." value={filters.search}
            onChange={(e) => onChange("search", e.target.value)} />
        </div>
      ),
    },
    { key: "stages", label: "Stage", list: options.stages },
    {
      key: "stageStatuses", label: "Stage status", count: filters.stageStatuses.length,
      body: <StageStatusGroups groups={options.stageStatuses} selected={filters.stageStatuses} onToggle={(v) => onToggle("stageStatuses", v)} />,
    },
    { key: "hiringStatuses", label: "Hiring status", list: options.hiringStatuses },
    { key: "recruiters", label: "Recruiter", list: options.recruiters },
  ];
  const countOf = (s) => ("list" in s ? filters[s.key].length : s.count || 0);

  // Open only sections that just became active, so a section the user closed stays closed.
  const activeKeys = sections.filter((s) => countOf(s) > 0).map((s) => s.key);
  const prevActiveRef = useRef(new Set());
  useEffect(() => {
    const newlyActive = activeKeys.filter((k) => !prevActiveRef.current.has(k));
    prevActiveRef.current = new Set(activeKeys);
    if (newlyActive.length) setOpen((prev) => new Set([...prev, ...newlyActive]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKeys.join(",")]);

  return (
    <aside className="candidates-v2-sidebar activity-filter-sidebar">
      <div className="sidebar-header-v2">
        <div className="sidebar-header-top-row">
          <div className="sidebar-title-v2">
            <FilterIcon />
            <span>Activity Filters</span>
            {activeCount > 0 && <span className="filter-badge">{activeCount}</span>}
          </div>
          <button type="button" className="activity-reset-btn" onClick={onReset} disabled={activeCount === 0}>
            <RefreshIcon size={13} />
            Reset
          </button>
        </div>
      </div>

      <div className="sidebar-accordion-v2">
        {sections.map((s) => {
          const isOpen = open.has(s.key);
          const count = countOf(s);
          return (
            <div key={s.key} className={`acc-item-v2 ${isOpen ? "open" : ""}`}>
              <button type="button" className="acc-btn-v2" onClick={() => toggleSection(s.key)}>
                <span className="acc-btn-left">
                  <span>{s.label}</span>
                  {count > 0 && <span className="filter-badge">{count}</span>}
                </span>
                <ChevronIcon open={isOpen} />
              </button>
              {isOpen && (
                <div className="acc-body-v2">
                  {"list" in s
                    ? <CheckList options={s.list} selected={filters[s.key]} onToggle={(v) => onToggle(s.key, v)} />
                    : s.body}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default ActivityFilterSidebar;
